import {
    ApiClientSettings,
    getActiveFontFamilies,
} from '@storefronts/common-types'
import Head from 'next/head'
import { useContext, useEffect } from 'react'
import { CustomizationContext } from '../context/customizationContext'

// Helper function to collect Google Font requirements
// eslint-disable-next-line complexity
const getConsolidatedGoogleFontParams = (
    settings: ApiClientSettings,
): string[] => {
    // Map: familyName -> { style -> Set<weight> }
    const googleFontsMap = new Map<string, Map<string, Set<number>>>()

    const fontFamilies = getActiveFontFamilies(settings)

    for (const family of fontFamilies) {
        // Ensure family and its files exist
        if (!family?.files) {
            continue
        }

        for (const fontFile of family.files) {
            // Ensure file has necessary properties
            if (
                !fontFile ||
                typeof fontFile.src !== 'string' ||
                typeof fontFile.weight !== 'number' ||
                typeof fontFile.style !== 'string'
            ) {
                console.warn('Skipping invalid font file data:', fontFile)
                continue
            }
            if (fontFile.type === 'GOOGLE') {
                // Replace '+' with space in family name before encoding
                const familyName = fontFile.src.replace(/\+/g, ' ') // Ensure spaces, not '+'
                const weight = fontFile.weight
                const style = fontFile.style.toLowerCase() // Ensure consistent case

                if (!googleFontsMap.has(familyName)) {
                    googleFontsMap.set(familyName, new Map())
                }
                const styleMap = googleFontsMap.get(familyName)!

                if (!styleMap.has(style)) {
                    styleMap.set(style, new Set())
                }
                const weightSet = styleMap.get(style)!
                weightSet.add(weight)
            }
        }
    }

    // Construct the URL parameters for Google Fonts API v2
    const urlParams: string[] = []
    for (const [familyName, styleMap] of googleFontsMap.entries()) {
        let familyParam = `family=${encodeURIComponent(familyName)}`
        const axisTags: string[] = []
        const variationTuples: string[] = []

        const italicWeights = styleMap.get('italic')
        const normalWeights = styleMap.get('normal')

        const hasItalic = italicWeights && italicWeights.size > 0
        const hasNormal = normalWeights && normalWeights.size > 0

        if (!hasItalic && !hasNormal) continue // Skip if no weights found for this family

        // Determine needed axes
        if (hasItalic) axisTags.push('ital')
        if (hasItalic || hasNormal) axisTags.push('wght')

        // Build the axis part of the query
        if (axisTags.length > 0) {
            familyParam += `:${axisTags.join(',')}`

            // Build the variation tuples (e.g., 0,400 for normal 400; 1,700 for italic 700)
            if (normalWeights) {
                normalWeights.forEach((w) =>
                    variationTuples.push(`${hasItalic ? '0,' : ''}${w}`),
                )
            }
            if (italicWeights) {
                italicWeights.forEach((w) =>
                    variationTuples.push(`${hasItalic ? '1,' : ''}${w}`),
                )
            }

            // Append sorted tuples to the parameter
            if (variationTuples.length > 0) {
                familyParam += `@${variationTuples.sort().join(';')}`
            }
        }
        urlParams.push(familyParam)
    }

    return urlParams
}

export const GoogleFontLoader: React.FC = () => {
    const customization = useContext(CustomizationContext)
    const googleFontParams = getConsolidatedGoogleFontParams(customization)

    // Construct the final URL if any font parameters were generated
    const googleFontUrl =
        googleFontParams.length > 0
            ? `https://fonts.googleapis.com/css2?${googleFontParams.join('&')}&display=swap`
            : null

    // The font URL is derived from per-client customization settings, so it can't be
    // hoisted into a static _document, and next/head warns against <link rel="stylesheet">
    // since it isn't guaranteed to land in the initial SSR output. Inject it manually
    // instead so we control when it's added/removed as the URL changes.
    useEffect(() => {
        if (!googleFontUrl) {
            return
        }

        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = googleFontUrl
        document.head.appendChild(link)

        return () => {
            document.head.removeChild(link)
        }
    }, [googleFontUrl])

    return (
        <Head>
            {/* Preconnect hints for performance */}
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link
                rel="preconnect"
                href="https://fonts.gstatic.com"
                crossOrigin="anonymous"
            />
            {/* Preload the Google Fonts CSS so the browser fetches it before hydration */}
            {googleFontUrl && (
                <link rel="preload" href={googleFontUrl} as="style" />
            )}
        </Head>
    )
}
